import { useMemo } from 'react';
import { Box } from '@mui/material';
import type { Highlight, HighlightLevel } from '../api/types';

/**
 * The scan with its highlights, in one coordinate system.
 *
 * One component for both the result thumbnail and the full viewer, because
 * coordinate rendering should have exactly one implementation: a fix to how
 * polygons scale has to reach every place they are drawn, and two copies of this
 * drift the first time one of them is touched.
 *
 * SVG rather than positioned divs — it takes polygons rather than rectangles,
 * scales without accumulating rounding error, and shares the image's box exactly
 * because both are laid out against the same viewBox.
 */

interface PageCanvasProps {
  /**
   * Object URL of the render, or null while it is being fetched. Null draws the
   * overlay over blank paper rather than nothing: the polygons are already known
   * — they come from a different request — and showing them as soon as they
   * arrive is what makes the page feel located rather than loaded.
   */
  imageUrl: string | null;
  /** Canonical render size. The viewBox, and the space every polygon lives in. */
  width: number;
  height: number;
  highlights: Highlight[];
  /** Index of the hit to emphasise; others are drawn subdued. */
  activeIndex?: number;
  /**
   * Frame tightly around this hit instead of showing the whole page. An A4 page
   * shrunk to a card is too small to find a word in; the crop is what makes a
   * result readable without opening it.
   */
  focusIndex?: number;
  /** Padding around the focused hit, as a fraction of the page width. */
  focusPad?: number;
  /**
   * Width/height the crop should end up at. Without it the crop takes the shape
   * of whatever word it framed, which in a fixed-size card is either letterboxed
   * or cut off; with it the short side grows into the surrounding page, so the
   * frame is filled with context instead of emptiness.
   */
  focusAspect?: number;
  onSelect?: (index: number) => void;
  alt: string;
}

/**
 * Precision is carried by SHAPE as well as colour: a solid outline is the word
 * itself, dashes mean the line is right but the word is a guess, dots mean only
 * the region is known. Colour alone would be invisible to a colour-blind reader
 * and, worse, would make an approximate highlight look exact.
 */
const levelStyle: Record<HighlightLevel, { stroke: string; fill: string; dash?: string }> = {
  word: { stroke: '#F5A524', fill: 'rgba(245,165,36,.28)' },
  line: { stroke: '#F5A524', fill: 'rgba(245,165,36,.16)', dash: '14 10' },
  block: { stroke: '#8892B0', fill: 'rgba(136,146,176,.12)', dash: '4 12' },
  unavailable: { stroke: 'transparent', fill: 'transparent' }
};

function pointsOf(polygon: number[][], width: number, height: number) {
  return polygon.map(([x, y]) => `${x * width},${y * height}`).join(' ');
}

/** Bounds of one hit in render pixels, across all its polygons. */
function boundsOf(hit: Highlight, width: number, height: number) {
  const xs = hit.polygons_norm.flat().map(([x]) => x * width);
  const ys = hit.polygons_norm.flat().map(([, y]) => y * height);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
}

export function PageCanvas({
  imageUrl, width, height, highlights, activeIndex, focusIndex,
  focusPad = 0.06, focusAspect, onSelect, alt
}: PageCanvasProps) {
  // The crop is expressed as a viewBox on both the image and the overlay, so
  // they cannot come apart: there is no second calculation to get wrong.
  const view = useMemo(() => {
    const whole = { x: 0, y: 0, w: width, h: height };
    if (focusIndex === undefined || !highlights[focusIndex]) return whole;

    const b = boundsOf(highlights[focusIndex], width, height);
    const pad = width * focusPad;
    let w = Math.min(width, b.x2 - b.x1 + pad * 2);
    let h = Math.min(height, b.y2 - b.y1 + pad * 2);

    if (focusAspect) {
      // Grow the short side only. Shrinking to fit would crop the hit itself,
      // which is the one thing the frame exists to show.
      if (w / h < focusAspect) w = Math.min(width, h * focusAspect);
      else h = Math.min(height, w / focusAspect);
    }

    // Centre on the hit, then slide back inside the page so a hit near an edge
    // still gets a full frame instead of a half-empty one.
    const cx = (b.x1 + b.x2) / 2;
    const cy = (b.y1 + b.y2) / 2;
    return {
      x: Math.max(0, Math.min(width - w, cx - w / 2)),
      y: Math.max(0, Math.min(height - h, cy - h / 2)),
      w,
      h
    };
  }, [focusAspect, focusIndex, focusPad, height, highlights, width]);

  if (!width || !height) return null;

  const viewBox = `${view.x} ${view.y} ${view.w} ${view.h}`;

  return (
    <Box sx={{ position: 'relative', width: '100%', lineHeight: 0 }}>
      <svg
        viewBox={viewBox}
        width="100%"
        role="img"
        aria-label={alt}
        style={{ display: 'block', background: '#fff' }}
      >
        {/* The image is placed in the same viewBox as the polygons, at full page
            size. Cropping is therefore purely a change of viewBox — nothing has
            to translate the overlay to match. */}
        {imageUrl && (
          <image href={imageUrl} x={0} y={0} width={width} height={height} preserveAspectRatio="none" />
        )}
        {highlights.map((hit, i) => {
          const style = levelStyle[hit.level] ?? levelStyle.block;
          const active = i === activeIndex;
          return (
            <g
              key={hit.match_id}
              onClick={onSelect ? () => onSelect(i) : undefined}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}
            >
              {hit.polygons_norm.map((polygon, j) => (
                <polygon
                  key={j}
                  points={pointsOf(polygon, width, height)}
                  fill={style.fill}
                  stroke={style.stroke}
                  strokeWidth={active ? 6 : 3}
                  strokeDasharray={style.dash}
                  opacity={activeIndex === undefined || active ? 1 : 0.55}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}
